/**
 * When has the visitor done THIS field? (Mark, 2026-10-12: the walkthroughs
 * are training, not a show. Each step tells the person what to do and waits
 * for them to do it; once they have, it says so and moves the ring on.)
 *
 * A form field has no server answer to wait for, so a field step's outcome
 * (`until.field`) is read off the control itself:
 *
 *   'picked'   a search picker (LwCombobox) holds a chosen record, and its
 *              list is closed: typing "Casc" is not choosing a customer.
 *   'chosen'   a select shows the option the step names (`label`, a prefix
 *              of the option's text), or the visitor made a choice of their
 *              own: a person who deliberately picks Tank is not stuck.
 *   'filled'   a text, date or time box has a value, and the visitor is
 *              finished with it: they left it (change, blur), they typed
 *              exactly what the step suggested (`value`), or they stopped
 *              typing for a moment. A PO half-typed is not finished, and the
 *              ring must not jump away from a box somebody is typing in.
 *   'checked'  a checkbox is ticked; with `off: true`, cleared (Retire a
 *              truck clears Active).
 *
 * `touched: true` on a 'filled' rule asks for the visitor's own edit as well:
 * a stop's Time starts out holding "now", and a box that was filled before
 * the step opened is not the visitor having set it.
 *
 * `redo: true`, on any rule, asks for an edit of the control after the step
 * opened before anything counts (spec 53 4.3): a trainee who goes Back to a
 * field "Do this step for me" filled must do it again themselves, and the
 * value the fallback left there must not finish the step on arrival.
 *
 * The page is re-read on every check (Vue may re-render the control), so the
 * watcher holds a selector and a label, never an element. Pure DOM: no Vue,
 * no router, testable in jsdom.
 */
import { controlIn, fieldIn } from './autoplay';

/** How long a visitor may pause mid-word before the field counts as done. */
export const SETTLE_MS = 2500;
const POLL_MS = 200;

function normalise(text) {
  return String(text || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** The control a field step is about: the target itself, or one inside it. */
export function controlFor(el, field = null) {
  if (!el) return null;
  const found = field ? fieldIn(el, field) : el;
  return controlIn(found);
}

/**
 * Whether `control` satisfies `rule` now.
 *
 * @param {Element|null} control
 * @param {{ field: string, value?: string, label?: string }} rule
 * @param {{ changed?: boolean, typed?: boolean, idleMs?: number, active?: boolean, settleMs?: number }} seen
 *   what the watcher has seen of the visitor since the step opened
 */
export function fieldDone(control, rule, seen = {}) {
  if (!control || !rule) return false;
  if (rule.redo && !seen.changed && !seen.typed) return false;
  const kind = rule.field;
  const value = String(control.value == null ? '' : control.value);
  if (kind === 'checked') return rule.off ? !control.checked : Boolean(control.checked);
  if (kind === 'picked') {
    const box = control.closest ? control.closest('[data-value-set]') : null;
    const set = box ? box.getAttribute('data-value-set') === 'true' : false;
    return set && control.getAttribute('aria-expanded') !== 'true' && value.trim() !== '';
  }
  if (kind === 'chosen') {
    if (control.tagName !== 'SELECT' || value === '') return false;
    if (!rule.label) return true;
    const option = control.options[control.selectedIndex];
    const text = normalise(option ? option.textContent : '');
    return text.startsWith(normalise(rule.label)) || Boolean(seen.changed);
  }
  if (kind === 'filled') {
    if (value.trim() === '') return false;
    if (rule.value && normalise(value) === normalise(rule.value)) return true;
    if (rule.touched && !seen.changed && !seen.typed) return false;
    if (seen.changed) return true;
    // Still being typed into, focused or not: "Do this step for me" types
    // without moving focus off the card (autoplay.js), and "not focused"
    // ended the PO step on its first letter (found filming the training
    // video, 2026-10-25).
    if (seen.typed && (seen.idleMs || 0) < (seen.settleMs || SETTLE_MS)) return false;
    if (!seen.active) return true;
    return (seen.idleMs || 0) >= (seen.settleMs || SETTLE_MS);
  }
  return false;
}

/**
 * Wait until the field the step names is done. Resolves the control, or null
 * when the signal aborts or the time runs out.
 *
 * @param {() => Element|null} locate the step's target, read afresh each time
 * @param {object} rule `until` of the step: { field, value?, label? }
 * @param {object} [options]
 * @param {string|null} [options.label] a label inside the target (`field` of the step)
 * @param {AbortSignal} [options.signal]
 * @param {number} [options.timeoutMs]
 * @param {number} [options.settleMs]
 */
export function waitForField(
  locate,
  rule,
  { label = null, signal = null, timeoutMs = 30 * 60000, settleMs = SETTLE_MS } = {}
) {
  return new Promise(resolve => {
    if (signal && signal.aborted) return resolve(null);
    let changed = false;
    let lastInput = 0;
    let done = false;
    const current = () => controlFor(locate(), label);
    const ours = target => {
      const control = current();
      return Boolean(control && target && (control === target || control.contains(target)));
    };
    const onInput = event => {
      if (ours(event.target)) lastInput = Date.now();
      check();
    };
    const onChange = event => {
      if (ours(event.target)) changed = true;
      check();
    };
    const finish = value => {
      if (done) return;
      done = true;
      clearInterval(timer);
      clearTimeout(limit);
      document.removeEventListener('input', onInput, true);
      document.removeEventListener('change', onChange, true);
      if (signal) signal.removeEventListener('abort', onAbort);
      resolve(value);
    };
    const onAbort = () => finish(null);
    function check() {
      if (done) return;
      const control = current();
      if (!control) return;
      const seen = {
        changed,
        typed: lastInput > 0,
        active: document.activeElement === control,
        idleMs: lastInput ? Date.now() - lastInput : 0,
        settleMs,
      };
      // Typing that has not paused yet is not idle, however long ago the
      // step opened: idleness is measured from the last keystroke only.
      if (seen.active && !lastInput) seen.idleMs = 0;
      if (fieldDone(control, rule, seen)) finish(control);
    }
    document.addEventListener('input', onInput, true);
    document.addEventListener('change', onChange, true);
    const timer = setInterval(check, POLL_MS);
    const limit = setTimeout(() => finish(null), timeoutMs);
    if (signal) signal.addEventListener('abort', onAbort, { once: true });
    check();
  });
}
